import { createInertiaApp } from '@inertiajs/react';
import { createElement, ReactNode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import Layout from '../layouts/Layout';
import { measurePerformance } from '../utils/performance';

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode;
  layout?: (page: ReactNode) => ReactNode;
};

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Enable progress bar for better perceived performance during page loads
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  progress: {
    color: '#3b82f6', // Blue color to match theme
    includeCSS: true,
    showSpinner: true,
  },

  resolve: async name => {
    // Exclude any file with .test. in the name
    // Use dynamic imports for automatic code splitting
    const pages = import.meta.glob<ResolvedComponent>(
      '../pages/**/!(*.test).tsx',
      { eager: false } // Ensure lazy loading
    );
    const pageLoader = pages[`../pages/${name}.tsx`];
    if (!pageLoader) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
      return null;
    }

    // Load page component dynamically for code splitting
    const page = await pageLoader();

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    if (page && typeof page === 'object' && 'default' in page) {
      (page.default as any).layout ||= (page: ReactNode) =>
        createElement(Layout, null, page);
    }
    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      const hasSsrContent = el.hasChildNodes();
      if (hasSsrContent) {
        hydrateRoot(el, createElement(App, props));
      } else {
        createRoot(el).render(createElement(App, props));
      }
      // Only run performance monitoring in development
      if (import.meta.env.DEV) {
        requestIdleCallback(() => {
          measurePerformance();
        });
      }
    } else {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.'
      );
    }
  },
});
